import { apiUrl } from '@/lib/api/origin';
import type { MetricDef, PeriodType } from '@/lib/api/queries';

/**
 * =============================================================================
 *  Metric Studio 的**純邏輯層** —— 「一個元件換 8 頁」真正住在這裡。
 *
 *  ROADMAP §6 的核心主張只有一個成立條件：**前端不得有任何硬寫的 metric key。**
 *  這個檔案是那句話最容易被違反的地方（「就這一個 metric 特別處理一下」都是從
 *  一個工具函式裡的 `if (metricKey === ...)` 開始的），所以它有一條硬規則：
 *
 *      這個檔案裡沒有、也不准有任何一個 metric_key 字面值。
 *
 *  每一個決定都只讀 `meta.metric_registry` 的欄位：`freq`、`category`、
 *  `compact_style`、`precision`、`unit`、`higher_is`、`feature_prefix`、
 *  `default_rule`、`href_template`。新增一列 registry ＝ 新增一頁，零行前端程式碼
 *  （驗收 #1 量的就是這件事）。
 * ========================================================================== */

/* ---------------------------------------------------------------- period_type */

/**
 * 這個 metric 吃不吃 `period_type`？
 *
 * **權威在後端**：`metric_resolver._PERIOD_TYPE_TABLES` 只認
 * `fin.statement_fact` 與 `fin.statement_wide` 兩張表，傳給其他指標一律 400
 * （`period_type_not_applicable`，而不是安靜忽略）。
 *
 * 但 `MetricDefOut` **沒有** `source_schema` / `source_table` 欄位——它們是儲存層的
 * 細節，registry 的公開檢視刻意不外流。前端手上唯一與「這是財報事實表」相關的
 * registry 欄位是 `freq`：財報三表與其衍生欄位是 `quarterly`，其餘（daily / weekly /
 * monthly）沒有一個來自 `fin.statement_*`。
 *
 * 所以這裡用 `freq === 'quarterly'` 當代理，並且**只用它決定「要不要顯示季／年切換」**：
 *   · 猜太寬（顯示了切換但後端不吃）→ 使用者按下去看到一次 400，頁面退回季度，
 *     不會有錯誤的數字。
 *   · 猜太窄（不顯示切換）→ 少一個功能，同樣不會有錯誤的數字。
 * 兩個失效方向都不會產生「以為自己拿到年報」的那一類 bug，而那是 §6「特別容易做錯」
 * 第 1 條真正在防的東西。
 *
 * ⚠️ 後端若在 `MetricDefOut` 加上一個明確欄位（例如 `period_typed: bool`），
 * 這個函式就該改成讀那個欄位，並把這段註解刪掉。
 */
export function isPeriodTyped(metric: Pick<MetricDef, 'freq'>): boolean {
  return metric.freq === 'quarterly';
}

/** 季／年切換的選項。`TTM` 由後端支援，但 UI 只在 registry 明說時才給。 */
export const PERIOD_TYPES: readonly PeriodType[] = ['Q', 'Y'] as const;

export const PERIOD_LABEL_ZH: Record<PeriodType, string> = {
  Q: '季度',
  Y: '年度',
  TTM: '近四季',
};

export function isPeriodType(value: unknown): value is PeriodType {
  return value === 'Q' || value === 'Y' || value === 'TTM';
}

/* --------------------------------------------------------------------- tabs */

/**
 * 一條路由底下的一個 tab（驗收 #5：財報三表**在一條路由下**切 tab）。
 *
 * `metricKey` 是這個 tab 真正要去 `/series` 拿的那一列 registry——
 * 也就是說「切 tab」在資料層就是「換一個 metric_key」，
 * 而**路由與捲軸位置完全不動**（只改 `?tab=`）。
 */
export interface StudioTab {
  readonly key: string;
  readonly labelZh: string;
  readonly metricKey: string;
}

interface DeclaredTab {
  key?: unknown;
  label_zh?: unknown;
  metric_key?: unknown;
}

/**
 * tab 從哪裡來？**兩條 registry 驅動的規則，沒有第三條，也沒有任何 metric key。**
 *
 *  1. **`default_rule.tabs`（明示）。** registry 列可以宣告一個
 *     `[{key, label_zh, metric_key}]` 陣列。這是財報三表（`?tab=income|balance|cashflow`）
 *     的正式契約：一列 `statements` 帶三個 tab，各自指向真正的事實 metric。
 *  2. **`feature_prefix` 兄弟（推導）。** 沒有明示宣告時，同一個 `feature_prefix`
 *     的其他列自動成為 tab（key ＝ metric_key，label ＝ label_zh），依 `sort_order` 排。
 *     這讓「三張表各自一列」的種法不必改前端也能得到同一個 tab 列。
 *
 * 兩條規則都只認欄位不認字串。少於兩個 tab 時回空陣列——一個 tab 的 tab 列
 * 是純粹的視覺雜訊。
 */
export function deriveTabs(metric: MetricDef, all: readonly MetricDef[]): StudioTab[] {
  const declared = readDeclaredTabs(metric);
  if (declared.length >= 2) return declared;

  const prefix = metric.feature_prefix;
  if (prefix === null || prefix === undefined || prefix === '') return [];
  const siblings = all
    .filter((m) => m.feature_prefix === prefix && m.studio_page)
    .sort((a, b) => a.sort_order - b.sort_order || a.metric_key.localeCompare(b.metric_key));
  if (siblings.length < 2) return [];
  return siblings.map((m) => ({ key: m.metric_key, labelZh: m.label_zh, metricKey: m.metric_key }));
}

function readDeclaredTabs(metric: MetricDef): StudioTab[] {
  const rule = metric.default_rule;
  if (rule === null || rule === undefined) return [];
  const raw = (rule as Record<string, unknown>)['tabs'];
  if (!Array.isArray(raw)) return [];
  const out: StudioTab[] = [];
  for (const entry of raw as DeclaredTab[]) {
    if (entry === null || typeof entry !== 'object') continue;
    const key = typeof entry.key === 'string' ? entry.key : undefined;
    const metricKey = typeof entry.metric_key === 'string' ? entry.metric_key : key;
    if (key === undefined || metricKey === undefined) continue;
    const labelZh = typeof entry.label_zh === 'string' ? entry.label_zh : key;
    out.push({ key, labelZh, metricKey });
  }
  return out;
}

/**
 * `?tab=` → 實際要拿的 metric_key。
 *
 * 認不得的 tab 值**退回第一個 tab**而不是報錯：一個被截斷或過期的分享連結
 * 應該退化成這一頁的預設視圖，不該變成錯誤頁（與 `__root.tsx` 對 `as_of` 的處理同理）。
 */
export function resolveTab(
  tabs: readonly StudioTab[],
  requested: string | undefined,
  fallbackMetricKey: string,
): { readonly active: StudioTab | null; readonly metricKey: string } {
  if (tabs.length === 0) return { active: null, metricKey: fallbackMetricKey };
  const hit = requested === undefined ? undefined : tabs.find((t) => t.key === requested);
  const active = hit ?? tabs[0];
  return { active, metricKey: active.metricKey };
}

/* -------------------------------------------------------------------- 副軸尺度 */

/**
 * 雙軸圖的副軸尺度。
 *
 * ROADMAP §6「風險（低）」逐字：「`DualAxisChart` 兩軸尺度在極端值時難看。
 * **對策：副軸一律用該 metric 的 1 年百分位而非原始值做預設，使用者可切回原始值。**」
 * 所以預設是 `pct`，不是 `raw`。
 */
export type ScaleMode = 'pct' | 'raw';

export const DEFAULT_SCALE: ScaleMode = 'pct';

export function isScaleMode(value: unknown): value is ScaleMode {
  return value === 'pct' || value === 'raw';
}

/* ---------------------------------------------------------------- CSV / table */

/**
 * `GET /{market}/securities/{symbol}/table/{metric_key}.csv` 的完整 URL。
 *
 * **它是一個 `<a href>`，不是一次 fetch。** 理由有二：
 *  · UTF-8 BOM（驗收 #3：Excel 開中文欄名不亂碼）由後端寫在 body 第一個 byte，
 *    而檔名走 `Content-Disposition`。兩者都只有瀏覽器原生下載會照做；
 *    用 fetch 接回來再自己造 Blob，等於在前端重新實作一次那兩件事，
 *    而重新實作的那一份遲早會忘記 BOM。
 *  · 走 `<a>` 就自動帶上 session cookie，不必碰 API client（而 `lib/api/queries.ts`
 *    是唯一准碰 client 的地方，見 asof-contract.test.ts 規則 1）。
 *
 * 網址經過 `apiUrl`：網站是相對路徑（同源）；iOS App 的建置接上 API_ORIGIN。
 * App 的 `<a download>` 帶不了 Bearer，所以那裡的下載按鈕由 MetricStudioPage 隱藏。
 */
export interface CsvHrefInput {
  readonly market: string;
  readonly symbol: string | null;
  readonly metricKey: string;
  readonly from: string;
  readonly to: string;
  readonly asOf?: string | undefined;
  readonly periodType?: PeriodType | undefined;
}

export function csvHref(input: CsvHrefInput): string {
  const params = new URLSearchParams({ from: input.from, to: input.to });
  if (input.asOf !== undefined) params.set('as_of', input.asOf);
  if (input.periodType !== undefined) params.set('period_type', input.periodType);
  const base =
    input.symbol === null
      ? `/api/v1/${input.market}/market/table/${encodeURIComponent(input.metricKey)}.csv`
      : `/api/v1/${input.market}/securities/${encodeURIComponent(input.symbol)}/table/${encodeURIComponent(input.metricKey)}.csv`;
  return `${apiUrl(base)}?${params.toString()}`;
}

/** 下載檔名。中文欄名靠 BOM，檔名靠這裡——兩者都不該讓使用者自己重新命名。 */
export function csvFileName(input: {
  symbol: string | null;
  metricKey: string;
  from: string;
  to: string;
}): string {
  const who = input.symbol ?? 'market';
  return `${who}_${input.metricKey}_${input.from}_${input.to}.csv`;
}

/* ------------------------------------------------------------------ 呈現輔助 */

/**
 * `higher_is` → 語意 class。**這裡沒有紅綠，只有 up / down / flat**，
 * 真正的顏色由 `<html data-market>` 決定（§10.6）。
 *
 * `context`（「需要上下文」）一律回 flat：對一個本身沒有好壞方向的指標
 * （例如本益比）把上漲畫成紅色，是在替使用者做一個 registry 明說不該做的判斷。
 */
export function deltaClass(
  delta: number | null | undefined,
  higherIs: MetricDef['higher_is'],
): 'text-up' | 'text-down' | 'text-flat' {
  if (delta === null || delta === undefined || delta === 0 || !Number.isFinite(delta)) {
    return 'text-flat';
  }
  if (higherIs === 'context') return 'text-flat';
  const bullish = higherIs === 'bullish' ? delta > 0 : delta < 0;
  return bullish ? 'text-up' : 'text-down';
}
